/**
 * Market Overview API – latest snapshot and optional history
 */

import { authenticatedFetch } from "./apiClient";
import { parseApiErrorBody } from "../utils/parseApiError";
import { throwIfNotOk } from "../utils/apiRequestError";
import type { MarketOverviewLatest, MarketOverviewHistoryItem } from "../types/marketOverview";

/**
 * Normalize display symbol for API (e.g. "XAU/USD" -> "XAUUSD")
 */
export function normalizeInstrumentForApi(instrument: string): string {
  return instrument.replace(/\//g, "");
}

/**
 * GET /api/market-overview?instrument={normalized}
 * Returns latest market snapshot for the instrument.
 */
export async function getMarketOverviewLatest(
  instrument: string
): Promise<MarketOverviewLatest> {
  const normalized = normalizeInstrumentForApi(instrument);
  const params = new URLSearchParams({ instrument: normalized });
  const response = await authenticatedFetch(`/market-overview?${params.toString()}`);

  if (!response.ok) {
    if (response.status === 404) {
      const body = await response.json().catch(() => ({}));
      const message = parseApiErrorBody(
        body && typeof body === "object" ? body : {},
        `No market overview data for instrument ${instrument}`
      ).message;
      throw new Error(message);
    }
    await throwIfNotOk(response, `Failed to fetch market overview: ${response.status}`);
  }

  return response.json();
}

export interface MarketOverviewHistoryOptions {
  start_time?: string | null;
  end_time?: string | null;
  limit?: number;
}

/**
 * GET /api/market-overview/history – for analytics (optional in v1)
 */
export async function getMarketOverviewHistory(
  instrument: string,
  options?: MarketOverviewHistoryOptions
): Promise<MarketOverviewHistoryItem[]> {
  const normalized = normalizeInstrumentForApi(instrument);
  const params = new URLSearchParams({ instrument: normalized });
  if (options?.start_time) params.set("start_time", options.start_time);
  if (options?.end_time) params.set("end_time", options.end_time);
  if (options?.limit != null) params.set("limit", String(options.limit));
  const response = await authenticatedFetch(`/market-overview/history?${params.toString()}`);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch market overview history: ${response.status}`);
  }

  return response.json();
}
